// Optional sub-segment picker for income / receipt rows.
//
// - meat_chickens segment   → choose a broiler batch (or none)
// - egg_chickens  segment   → choose a layer flock (or none)
// - cattle        segment   → choose an individual animal (or none = whole herd)
//
// "None" is always the default — Jeff's rule: never force a pick.
//
// Lazy-loads the option list on first render and caches it for the life of
// the component.

import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

export default function SubsegmentPicker({ segmentId, value, onChange, compact = false }) {
  const kind = kindForSegment(segmentId);

  const [opts, setOpts] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!kind) return;
    let cancelled = false;
    (async () => {
      try {
        if (kind === 'chicken_batch') {
          // Fetch all batches (incl. retired) so a transaction tagged to a
          // retired batch still resolves to its name in the picker.
          const { batches } = await api.get('/api/chickens?all=1');
          if (cancelled) return;
          const wantType = segmentId === 'meat_chickens' ? 'broiler' : 'layer';
          setOpts(batches.filter(b => b.type === wantType).map(b => ({
            id: b.id,
            label: `${b.name}${b.status !== 'active' ? ` · ${b.status}` : ''}${b.on_hand != null ? ` · ${b.on_hand} on hand` : ''}`,
          })));
        } else if (kind === 'cattle') {
          const { cattle } = await api.get('/api/cattle?all=1');
          if (cancelled) return;
          setOpts(cattle.map(c => ({
            id: c.id,
            label: `${c.ear_tag || `#${c.id}`}${c.name ? ` — ${c.name}` : ''}${c.sex ? ` (${c.sex})` : ''}${c.disposition !== 'active' ? ` · ${c.disposition}` : ''}`,
          })));
        }
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, [segmentId, kind]);

  if (!kind) return null;

  const placeholder = kind === 'chicken_batch'
    ? (segmentId === 'meat_chickens' ? 'Whole segment (no specific batch)' : 'Whole flock')
    : 'Whole herd (no specific animal)';

  const label = kind === 'chicken_batch'
    ? (segmentId === 'meat_chickens' ? 'Broiler batch (optional)' : 'Layer flock (optional)')
    : 'Animal (optional)';

  const fieldName = kind === 'chicken_batch' ? 'chicken_batch_id' : 'cattle_id';

  return (
    <div className={compact ? '' : 'col-span-2'}>
      {!compact && <label className="label">{label}</label>}
      <select
        className="input"
        value={value ?? ''}
        onChange={e => {
          const v = e.target.value === '' ? null : Number(e.target.value);
          onChange({ [fieldName]: v });
        }}
      >
        <option value="">{placeholder}</option>
        {/* While opts loads, or if the saved value isn't in opts (e.g. a batch
            from a different chicken type), synthesize an option so the select
            displays correctly instead of falling back to the placeholder. */}
        {value != null && (opts === null || !opts.some(o => o.id === value)) && (
          <option value={value}>
            (id #{value}{opts === null ? ' · loading details…' : ' · not in current list'})
          </option>
        )}
        {opts?.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      {err && <p className="text-xs text-rust-700 mt-1">{err}</p>}
    </div>
  );
}

function kindForSegment(segmentId) {
  if (segmentId === 'meat_chickens' || segmentId === 'egg_chickens') return 'chicken_batch';
  if (segmentId === 'cattle') return 'cattle';
  return null;
}
